import { useCallback, useEffect, useRef, useState } from "preact/hooks";

export function useChangeEffect(effect: () => void, deps: any[]) {
  const isMounted = useRef(false);
  const effectRef = useRef(effect);
  effectRef.current = effect;
  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    effectRef.current();
  }, deps);
}

type UseResizeObserverResult = {
  entry: ResizeObserverEntry | null;
  ref: import("preact").RefObject<HTMLElement>;
};

/* c8 ignore start */
export function useResizeObserver(
  options: ResizeObserverOptions | undefined,
): UseResizeObserverResult {
  const ref = useRef(null);
  const [entry, setEntry] = useState<ResizeObserverEntry | null>(() => null);
  useEffect(() => {
    if (ref.current === null) return;
    const observer = new ResizeObserver(([entry]) => {
      setEntry(entry);
    });
    observer.observe(ref.current, options);
    return () => {
      observer.disconnect();
    };
  }, [ref.current]);
  return {
    entry,
    ref,
  };
}

export function useResizeObserverEntryHeight(
  entry: ResizeObserverEntry | null,
  onHeight: (value: number) => void,
) {
  useEffect(() => {
    const height = entry?.contentRect.height;
    if (height === undefined) return;
    onHeight(height);
  }, [entry?.contentRect?.height, onHeight]);
}
/* c8 ignore end */

export function useUpdate() {
  const [_, setNow] = useState(() => Date.now());
  return useCallback(() => {
    setNow(Date.now());
  }, [setNow]);
}
